import { Link } from 'react-router-dom';
import Navbar from '../home/components/Navbar';
import Footer from '../home/components/Footer';

const DiabetesInsulinResistanceFH = () => {
  const conditions = [
    {
      number: '1',
      title: 'Accelerated Atherosclerosis',
      content: 'In FH, arteries are exposed to high LDL from birth. In diabetes: High glucose damages the endothelium, advanced glycation end products (AGEs) stiffen vessels, and oxidative stress increases. When combined, plaque forms faster and becomes more unstable. Patients with both conditions often show: Earlier coronary artery disease, multi-vessel involvement, and higher coronary calcium scores.',
      icon: 'ri-speed-line',
      color: '#f04e58'
    },
    {
      number: '2',
      title: 'Endothelial Dysfunction Amplified',
      content: 'The endothelium regulates blood flow and prevents clot formation. Both FH and diabetes independently impair endothelial function. Together they: Increase oxidative stress, reduce nitric oxide production, and promote inflammation. This creates a vascular environment highly prone to plaque progression and clot formation.',
      icon: 'ri-heart-pulse-line',
      color: '#0d9488'
    },
    {
      number: '3',
      title: 'Atherogenic Lipid Pattern in Insulin Resistance',
      content: 'While FH is primarily an LDL disorder, insulin resistance often produces: Elevated triglycerides, low HDL, and small dense LDL particles. Small dense LDL particles are particularly dangerous because they: Penetrate artery walls more easily, are more prone to oxidation, and trigger inflammation. This worsens the already elevated LDL burden in FH.',
      icon: 'ri-drop-line',
      color: '#e67e22'
    },
    {
      number: '4',
      title: 'Higher Risk of Coronary Events',
      content: 'Studies show that individuals with FH who also develop type 2 diabetes have: Increased risk of myocardial infarction, higher likelihood of needing revascularization, and greater long-term cardiovascular mortality. The additive effect of LDL-driven plaque and glucose-driven vascular injury significantly raises event risk.',
      icon: 'ri-heart-add-line',
      color: '#8b5cf6'
    },
    {
      number: '5',
      title: 'Residual Risk Despite LDL Control',
      content: 'Even when LDL is aggressively lowered: Insulin resistance may persist, inflammation may remain elevated, and glucose variability continues to stress arteries. This means comprehensive management must address both lipid and metabolic health.',
      icon: 'ri-alert-line',
      color: '#f59e0b'
    },
    {
      number: '6',
      title: 'Does FH Increase Diabetes Risk?',
      content: 'Interestingly, some research suggests that people with FH may have a slightly lower incidence of type 2 diabetes compared to the general population. Possible explanations include: Altered LDL receptor function and genetic metabolic differences. However, when diabetes does develop in FH patients — particularly due to obesity, sedentary lifestyle, or aging — cardiovascular risk becomes significantly elevated.',
      icon: 'ri-question-line',
      color: '#3b82f6'
    },
    {
      number: '7',
      title: 'The Role of Statins',
      content: 'Statins are first-line therapy for FH. While statins slightly increase blood sugar levels in some individuals, the cardiovascular benefits overwhelmingly outweigh the small diabetes risk. For patients with both FH and diabetes: High-intensity statin therapy remains critical, LDL targets are often more aggressive (&lt;55 mg/dL in very high risk patients), and stopping statins due to mild glucose elevation can be dangerous in FH.',
      icon: 'ri-medicine-bottle-line',
      color: '#ec4899'
    },
    {
      number: '8',
      title: 'Management Strategy for Dual Risk',
      content: 'Optimal care includes: <strong>Aggressive LDL Reduction</strong> (High-intensity statins, Ezetimibe, PCSK9 inhibitors, Inclisiran, LDL apheresis in severe cases); <strong>Glucose Control</strong> (Lifestyle modification, Metformin, GLP-1 receptor agonists, SGLT2 inhibitors which may also provide cardiovascular benefit); <strong>Inflammation Reduction</strong> (Weight management, Exercise, Blood pressure control). Comprehensive risk management is essential.',
      icon: 'ri-stethoscope-line',
      color: '#10b981'
    },
    {
      number: '9',
      title: 'Screening Considerations',
      content: 'Patients with FH should routinely be screened for: Fasting glucose, HbA1c, and insulin resistance markers. Similarly, individuals with early heart disease and diabetes should be evaluated for possible FH if LDL levels exceed 190 mg/dL or there is a family history of premature cardiovascular disease.',
      icon: 'ri-test-tube-line',
      color: '#06b6d4'
    },
    {
      number: '10',
      title: 'Prevention Is Lifelong',
      content: 'The key concept in FH is cholesterol-year burden — cumulative lifetime LDL exposure. When diabetes or insulin resistance is added, vascular aging accelerates even further. Early diagnosis of FH allows: Earlier lipid control, prevention of metabolic complications, and family cascade screening. Reducing LDL early in life dramatically lowers long-term event risk — even in those who later develop diabetes.',
      icon: 'ri-time-line',
      color: '#a855f7'
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      {/* Hero Section */}
      <section className="pt-28 pb-16 bg-gradient-to-br from-[#fef9f9] via-[#fef5f5] to-[#faf5f8]">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-12">
            <span className="inline-flex items-center gap-2 px-4 py-2 bg-white rounded-full text-sm text-gray-600 mb-6 shadow-sm">
              <i className="ri-heart-pulse-line text-[#f04e58]"></i>
              Understanding the Dangerous Combination
            </span>
            <h1 className="text-4xl lg:text-5xl font-bold text-gray-900 leading-tight mb-6">
              Diabetes & Insulin Resistance + <br />
              Familial Hypercholesterolemia (FH)
            </h1>
            <p className="text-gray-600 text-lg leading-relaxed max-w-3xl mx-auto">
              When FH coexists with diabetes or insulin resistance, cardiovascular risk rises dramatically
            </p>
          </div>
        </div>
      </section>

      {/* Introduction Section */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-6">
          <div className="mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">
              Familial Hypercholesterolemia (FH) with Diabetes & Insulin Resistance: A Dangerous Combination
            </h2>
            <div className="prose prose-lg max-w-none">
              <p className="text-gray-700 leading-relaxed mb-4">
                Most people think of high cholesterol and diabetes as separate health issues.
              </p>
              <p className="text-gray-700 leading-relaxed mb-4">
                But when Familial Hypercholesterolemia (FH) coexists with diabetes or insulin resistance, cardiovascular risk rises dramatically.
              </p>
              <p className="text-gray-700 leading-relaxed mb-4">
                FH already exposes arteries to lifelong elevated LDL cholesterol. When combined with metabolic conditions like type 2 diabetes or insulin resistance, vascular damage accelerates — increasing the likelihood of heart attack, stroke, and long-term complications.
              </p>
              <p className="text-gray-700 leading-relaxed mb-4">
                Understanding this interaction is critical for prevention.
              </p>
            </div>
          </div>

          {/* Understanding the Conditions */}
          <div className="grid md:grid-cols-2 gap-6 mb-12">
            <div className="bg-gradient-to-br from-[#f04e58]/10 to-white rounded-2xl p-8 border border-[#f04e58]/20">
              <h3 className="text-xl font-bold text-gray-900 mb-4">
                What is Familial Hypercholesterolemia?
              </h3>
              <p className="text-gray-700 leading-relaxed mb-4">
                FH is a genetic disorder characterized by:
              </p>
              <ul className="space-y-2">
                <li className="flex items-start gap-3">
                  <i className="ri-checkbox-circle-fill text-[#f04e58] text-xl flex-shrink-0 mt-1"></i>
                  <span className="text-gray-700">Very high LDL cholesterol from birth</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-checkbox-circle-fill text-[#f04e58] text-xl flex-shrink-0 mt-1"></i>
                  <span className="text-gray-700">Impaired LDL clearance</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-checkbox-circle-fill text-[#f04e58] text-xl flex-shrink-0 mt-1"></i>
                  <span className="text-gray-700">Accelerated atherosclerosis</span>
                </li>
              </ul>
              <p className="text-gray-700 leading-relaxed mt-4">
                It affects about 1 in 250 people worldwide and often goes undiagnosed.
              </p>
            </div>

            <div className="bg-gradient-to-br from-[#0d9488]/10 to-white rounded-2xl p-8 border border-[#0d9488]/20">
              <h3 className="text-xl font-bold text-gray-900 mb-4">
                What is Insulin Resistance?
              </h3>
              <p className="text-gray-700 leading-relaxed mb-4">
                Insulin resistance occurs when cells do not respond properly to insulin, leading to:
              </p>
              <ul className="space-y-2">
                <li className="flex items-start gap-3">
                  <i className="ri-checkbox-circle-fill text-[#0d9488] text-xl flex-shrink-0 mt-1"></i>
                  <span className="text-gray-700">Elevated blood sugar</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-checkbox-circle-fill text-[#0d9488] text-xl flex-shrink-0 mt-1"></i>
                  <span className="text-gray-700">Increased insulin production</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-checkbox-circle-fill text-[#0d9488] text-xl flex-shrink-0 mt-1"></i>
                  <span className="text-gray-700">Higher triglycerides</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-checkbox-circle-fill text-[#0d9488] text-xl flex-shrink-0 mt-1"></i>
                  <span className="text-gray-700">Reduced HDL ("good") cholesterol</span>
                </li>
              </ul>
              <p className="text-gray-700 leading-relaxed mt-4">
                Over time, insulin resistance can progress to type 2 diabetes.
              </p>
            </div>
          </div>

          {/* Why FH + Diabetes Is So Concerning */}
          <div className="bg-gradient-to-r from-[#f04e58] to-[#e67e22] rounded-2xl p-8 text-white mb-12">
            <h3 className="text-2xl font-bold mb-4">Why FH + Diabetes Is So Concerning</h3>
            <p className="text-white/95 leading-relaxed mb-4">
              Both conditions independently increase cardiovascular risk.
            </p>
            <p className="text-white/95 leading-relaxed mb-4">
              When they occur together:
            </p>
            <ul className="space-y-2 mb-4">
              <li className="flex items-center gap-2">
                <i className="ri-arrow-right-s-fill text-xl"></i>
                <span>LDL burden remains high</span>
              </li>
              <li className="flex items-center gap-2">
                <i className="ri-arrow-right-s-fill text-xl"></i>
                <span>Blood sugar damages vessel lining</span>
              </li>
              <li className="flex items-center gap-2">
                <i className="ri-arrow-right-s-fill text-xl"></i>
                <span>Inflammation increases</span>
              </li>
              <li className="flex items-center gap-2">
                <i className="ri-arrow-right-s-fill text-xl"></i>
                <span>Plaque formation accelerates</span>
              </li>
            </ul>
            <p className="text-white font-semibold text-lg">
              This combination significantly increases risk of premature cardiovascular disease.
            </p>
          </div>

          {/* Image */}
          <div className="w-full h-[400px] rounded-2xl overflow-hidden mb-12">
            <img
              src="https://public.readdy.ai/ai/img_res/f037464c05e9bbfcfc6f899f446350b1.jpg"
              alt="Diabetes and FH combined cardiovascular risk"
              className="w-full h-full object-cover object-top"
            />
          </div>
        </div>
      </section>

      {/* Key Interactions Section */}
      <section className="py-20 bg-gradient-to-br from-[#fef9f9] via-white to-[#faf5f8]">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              How FH and Diabetes Interact
            </h2>
            <p className="text-gray-600 leading-relaxed max-w-3xl mx-auto">
              Ten critical ways these conditions amplify cardiovascular risk
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            {conditions.map((item, index) => (
              <div
                key={index}
                className="bg-white rounded-2xl p-6 shadow-sm hover:shadow-lg transition-all duration-300 border border-gray-100"
              >
                <div className="flex items-start gap-4 mb-4">
                  <div
                    className="w-12 h-12 flex items-center justify-center rounded-xl flex-shrink-0"
                    style={{ backgroundColor: `${item.color}15` }}
                  >
                    <i className={`${item.icon} text-xl`} style={{ color: item.color }}></i>
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <div
                        className="w-8 h-8 flex items-center justify-center rounded-full text-white font-bold text-sm"
                        style={{ backgroundColor: item.color }}
                      >
                        {item.number}
                      </div>
                      <h3 className="text-lg font-semibold text-gray-900">
                        {item.title}
                      </h3>
                    </div>
                    <p className="text-sm text-gray-600 leading-relaxed" dangerouslySetInnerHTML={{ __html: item.content }}></p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Raising Awareness Section */}
      <section className="py-20 bg-gradient-to-br from-[#0d9488]/5 via-white to-[#f04e58]/5">
        <div className="max-w-4xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              Raising Awareness
            </h2>
            <p className="text-gray-700 leading-relaxed mb-6">
              Many people living with FH do not know they have it.
            </p>
            <p className="text-gray-700 leading-relaxed mb-6">
              When diabetes is diagnosed first, clinicians may attribute cardiovascular risk solely to glucose — missing the underlying genetic lipid disorder.
            </p>
          </div>

          <div className="bg-white rounded-2xl p-8 shadow-lg border border-gray-100 mb-8">
            <h3 className="text-xl font-bold text-gray-900 mb-4">
              If you or a family member has:
            </h3>
            <ul className="space-y-3">
              <li className="flex items-start gap-3">
                <i className="ri-checkbox-circle-fill text-[#f04e58] text-xl flex-shrink-0 mt-1"></i>
                <span className="text-gray-700">LDL above 190 mg/dL</span>
              </li>
              <li className="flex items-start gap-3">
                <i className="ri-checkbox-circle-fill text-[#f04e58] text-xl flex-shrink-0 mt-1"></i>
                <span className="text-gray-700">Early heart disease</span>
              </li>
              <li className="flex items-start gap-3">
                <i className="ri-checkbox-circle-fill text-[#f04e58] text-xl flex-shrink-0 mt-1"></i>
                <span className="text-gray-700">Family history of premature cardiovascular events</span>
              </li>
              <li className="flex items-start gap-3">
                <i className="ri-checkbox-circle-fill text-[#f04e58] text-xl flex-shrink-0 mt-1"></i>
                <span className="text-gray-700">Diabetes with unusually high LDL</span>
              </li>
            </ul>
            <p className="text-gray-900 font-semibold mt-6 text-lg">
              Ask about evaluation for Familial Hypercholesterolemia.
            </p>
          </div>
        </div>
      </section>

      {/* Final Takeaway Section */}
      <section className="py-20 bg-gradient-to-br from-gray-900 to-gray-800 text-white">
        <div className="max-w-4xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold mb-6">Final Takeaway</h2>
            <p className="text-white/90 leading-relaxed mb-4 text-lg">
              Familial Hypercholesterolemia alone increases lifetime cardiovascular risk.
            </p>
            <p className="text-white/90 leading-relaxed mb-4 text-lg">
              Diabetes and insulin resistance alone increase cardiovascular risk.
            </p>
            <p className="text-white font-semibold text-xl mb-8">
              Together, they create a powerful driver of accelerated vascular disease.
            </p>
          </div>

          <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 mb-8">
            <h3 className="text-2xl font-bold mb-6 text-center">The Good News</h3>
            <p className="text-white/90 leading-relaxed text-center text-lg mb-6">
              With early identification and aggressive management, the risk can be dramatically reduced.
            </p>
            <p className="text-white font-semibold text-center text-xl">
              Awareness is the first step — and it may protect not just one individual, but an entire family.
            </p>
          </div>

          <div className="flex justify-center">
            <Link
              to="/consultation"
              className="inline-flex items-center px-8 py-4 bg-[#f04e58] text-white rounded-full font-semibold hover:bg-[#d93d47] transition-all cursor-pointer whitespace-nowrap shadow-lg"
            >
              Get Evaluated Today
              <i className="ri-arrow-right-line ml-2"></i>
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default DiabetesInsulinResistanceFH;
